import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fetchWorkPosts, fetchWorkFacets, fetchWorkGroups } from "@/lib/api";
import { toWorkItem } from "@/lib/workItem";
import { Nav } from "@/components/Nav";
import { ViewSwitcher } from "@/components/ViewSwitcher";
import { WorkFilterBar } from "@/components/WorkFilterBar";
import { Filmstrip } from "@/components/Filmstrip";
import { WorkGroupedGrid } from "@/components/WorkGroupedGrid";
import { WorkChronologicalGrid } from "@/components/WorkChronologicalGrid";
import { ListView } from "@/components/ListView";
import { WORK } from "@/constants/testIds";

const PAGE_SIZE = 24;

export default function WorkCatalog({ defaultAiOnly }) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const [searchDraft, setSearchDraft] = useState(params.get("q") || "");

  // React Router's setSearchParams functional updater can read a stale snapshot when two
  // updates fire in quick succession before a re-render lands (e.g. Clear immediately followed
  // by picking a filter). We keep our own ref as the single source of truth for "latest intended
  // params" so every handler composes on top of the previous one synchronously, not on the URL.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const commitParams = (mutator, { replace = true } = {}) => {
    const next = new URLSearchParams(paramsRef.current);
    mutator(next);
    paramsRef.current = next;
    setParams(next, { replace });
  };

  const view = params.get("view") || "grid";
  const q = params.get("q") || "";
  const category = params.get("category") || "all";
  const account = params.get("account") || "all";
  const aiOnly = params.get("aiOnly") === "1" || (defaultAiOnly && params.get("aiOnly") !== "0");
  const yearFrom = params.get("yearFrom") || "all";
  const yearTo = params.get("yearTo") || "all";
  const sort = ["latest", "oldest", "featured"].includes(params.get("sort")) ? params.get("sort") : "latest";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const isFeaturedGrid = view === "grid" && sort === "featured";

  const searchDebounceRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [page, sort]);

  useEffect(() => {
    // Back/forward must restore the URL's search text too, not later overwrite it
    // with a draft left over from a different sort/filter history entry.
    clearTimeout(searchDebounceRef.current);
    setSearchDraft(params.get("q") || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    searchDebounceRef.current = setTimeout(() => {
      if (searchDraft !== (paramsRef.current.get("q") || "")) {
        commitParams((p) => {
          if (!searchDraft) p.delete("q");
          else p.set("q", searchDraft);
          p.set("page", "1");
        });
      }
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  const { data: facets } = useQuery({ queryKey: ["work-facets"], queryFn: fetchWorkFacets });

  const apiFilters = {
    q: q || undefined,
    category: category === "all" ? undefined : category,
    account: account === "all" ? undefined : account,
    aiOnly: aiOnly ? true : undefined,
    yearFrom: yearFrom === "all" ? undefined : yearFrom,
    yearTo: yearTo === "all" ? undefined : yearTo,
    sort,
  };

  const { data: groupCounts, isLoading: groupsLoading, isError: groupsError } = useQuery({
    queryKey: ["work-groups", q, category, account, aiOnly, yearFrom, yearTo],
    queryFn: () => fetchWorkGroups(apiFilters),
    enabled: isFeaturedGrid,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ["work", q, category, account, aiOnly, yearFrom, yearTo, sort, page],
    queryFn: () => fetchWorkPosts({ ...apiFilters, page, pageSize: PAGE_SIZE }),
    enabled: !isFeaturedGrid,
  });

  const commitWithFlush = (mutator, options) => {
    clearTimeout(searchDebounceRef.current);
    commitParams((p) => {
      if (searchDraft !== (paramsRef.current.get("q") || "")) {
        if (!searchDraft) p.delete("q");
        else p.set("q", searchDraft);
      }
      mutator(p);
    }, options);
  };

  const setView = (v) => commitWithFlush((p) => p.set("view", v));
  const setSort = (value) => commitWithFlush((p) => {
    p.set("sort", value);
    p.set("page", "1");
  }, { replace: false });
  const setCategory = (c) => commitWithFlush((p) => { c === "all" ? p.delete("category") : p.set("category", c); p.set("page", "1"); });
  const setAccount = (a) => commitWithFlush((p) => { a === "all" ? p.delete("account") : p.set("account", a); p.set("page", "1"); });
  const setAiOnly = (v) => commitWithFlush((p) => { v ? p.set("aiOnly", "1") : p.set("aiOnly", "0"); p.set("page", "1"); });
  const setYearFrom = (y) => commitWithFlush((p) => { y === "all" ? p.delete("yearFrom") : p.set("yearFrom", y); p.set("page", "1"); });
  const setYearTo = (y) => commitWithFlush((p) => { y === "all" ? p.delete("yearTo") : p.set("yearTo", y); p.set("page", "1"); });
  const setPage = (n) => {
    const hasPendingSearch = searchDraft !== (paramsRef.current.get("q") || "");
    commitWithFlush((p) => p.set("page", String(hasPendingSearch ? 1 : n)), { replace: false });
  };
  const clearAll = () => {
    clearTimeout(searchDebounceRef.current);
    setSearchDraft("");
    commitParams((p) => {
      ["q", "category", "account", "aiOnly", "yearFrom", "yearTo"].forEach((k) => p.delete(k));
      p.set("page", "1");
    });
  };

  const items = useMemo(() => (data?.items || []).map(toWorkItem), [data]);
  const totalPages = data?.totalPages ?? 0;
  const resultCount = isFeaturedGrid ? (groupCounts?.total ?? 0) : (data?.total ?? 0);
  const loading = isFeaturedGrid ? groupsLoading : isLoading;
  const errored = isFeaturedGrid ? groupsError : isError;
  const empty = resultCount === 0;

  return (
    <div className="min-h-screen bg-[#101010] text-[#F5F5F0]" data-testid={WORK.page}>
      <Nav rightSlot={<ViewSwitcher view={view} onChange={setView} />} />
      <WorkFilterBar
        facets={facets}
        search={searchDraft}
        category={category}
        account={account}
        aiOnly={aiOnly}
        yearFrom={yearFrom}
        yearTo={yearTo}
        sort={sort}
        onSearch={setSearchDraft}
        onCategory={setCategory}
        onAccount={setAccount}
        onAiOnly={setAiOnly}
        onYearFrom={setYearFrom}
        onYearTo={setYearTo}
        onSort={setSort}
        onClear={clearAll}
        resultCount={resultCount}
        totalCount={facets?.total ?? resultCount}
      />

      {loading && (
        <div className="flex items-center justify-center h-[50vh] text-xs uppercase tracking-widest text-[#555550]" data-testid={WORK.loadingState}>
          Loading portfolio…
        </div>
      )}
      {!loading && errored && (
        <div className="flex items-center justify-center h-[50vh] text-xs uppercase tracking-widest text-[#888880]" data-testid={WORK.errorState}>
          Could not load the portfolio. Please retry shortly.
        </div>
      )}
      {!loading && !errored && empty && (
        <div className="flex items-center justify-center h-[40vh] text-sm text-[#888880]" data-testid={WORK.emptyState}>
          No films match these filters.
        </div>
      )}

      {!loading && !errored && !empty && (
        <>
          {view === "slider" && (
            <div className="pt-6 sm:pt-8">
              <Filmstrip key={`${JSON.stringify(apiFilters)}:${page}`} projects={items} searchString={location.search} linkBase="/film" useNaturalRatio={false} />
            </div>
          )}
          {isFeaturedGrid && (
            <WorkGroupedGrid key={JSON.stringify(apiFilters)} filters={apiFilters} groupCounts={groupCounts} searchString={location.search} linkBase="/film" />
          )}
          {view === "grid" && !isFeaturedGrid && (
            <WorkChronologicalGrid
              items={items}
              startRank={(page - 1) * PAGE_SIZE + 1}
              total={resultCount}
              searchString={location.search}
            />
          )}
          {view === "list" && <ListView projects={items} searchString={location.search} linkBase="/film" />}

          {!isFeaturedGrid && (
            <div className="flex items-center justify-center gap-4 pb-16 sm:pb-20">
              <button
                type="button"
                data-testid={WORK.prevPage}
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-3 min-h-[40px] border border-[#2A2A2A] text-[#888880] hover:text-[#CCFF00] hover:border-[#CCFF00] disabled:opacity-30 disabled:pointer-events-none transition-colors"
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <span data-testid={WORK.pageInfo} className="text-xs text-[#555550]">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                data-testid={WORK.nextPage}
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-3 min-h-[40px] border border-[#2A2A2A] text-[#888880] hover:text-[#CCFF00] hover:border-[#CCFF00] disabled:opacity-30 disabled:pointer-events-none transition-colors"
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
